// client/src/components/admin/SuperAdminNonprofitTab.jsx
//
// Platform Admin → Nonprofits: review queue for nonprofit discount
// applications. Approve (puts the Stripe coupon on the tenant's
// subscription, or holds it until they subscribe), reject or revoke, each
// with a note. Every decision is recorded in the application's trail and in
// Transactions.
//
// API: /api/super-admin/nonprofit (server/routes/superAdminNonprofitRoutes.js).

import React, { useCallback, useEffect, useState } from 'react';
import {
  listApplications, getApplication, revealRegistration, decide, proofUrl, errorText,
} from '../../services/nonprofitApi';
import { RefreshButton } from '../common/IconButton';

const FILTERS = ['pending', 'approved', 'rejected', 'revoked', 'withdrawn', 'all'];
const BADGE = { pending: 'warn', approved: 'enabled', rejected: 'active', revoked: 'active', withdrawn: 'disabled' };
const ACTION_LABEL = {
  submitted: 'Submitted', withdrawn: 'Withdrawn by tenant', approved: 'Approved', rejected: 'Rejected', revoked: 'Revoked',
  discount_applied: 'Discount applied in Stripe', discount_failed: 'Discount not applied', discount_removed: 'Discount removed in Stripe',
  registration_viewed: 'Full registration number viewed',
};

function fmt(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function Detail({ id, onChanged, notify }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError('');
    getApplication(id).then(setD).catch((e) => setError(errorText(e, 'Failed to load the application.')));
  }, [id]);

  useEffect(() => { load(); setReg(null); setNote(''); }, [load]);

  const act = async (action) => {
    const a = d.application;
    const confirmText = {
      approve: `Approve ${a.orgName}? The nonprofit coupon goes on their Stripe subscription and they are emailed.`,
      reject: `Reject ${a.orgName}? They are emailed your note.`,
      revoke: `Revoke the discount for ${a.orgName}? The coupon is removed from their subscription and they are emailed.`,
      'retry-discount': `Try applying the Stripe coupon for ${a.orgName} again?`,
    }[action];
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const r = await decide(id, action, note);
      setD(r);
      setNote('');
      const extra = r.discount && r.discount.error ? ` Stripe: ${r.discount.error}` : r.removeError ? ` Stripe: ${r.removeError}` : '';
      notify(`${ACTION_LABEL[action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : action === 'revoke' ? 'revoked' : 'discount_applied']} — ${a.orgName}.${extra}`);
      onChanged();
    } catch (e) {
      notify(errorText(e, 'Could not save the decision.'));
    } finally {
      setBusy(false);
    }
  };

  const reveal = async () => {
    try { setReg(await revealRegistration(id)); load(); } catch (e) { notify(errorText(e, 'Could not reveal the number.')); }
  };

  if (error) return <p className="sa-count-bad">{error}</p>;
  if (!d) return <p className="sa-loading">Loading…</p>;
  const a = d.application;
  return (
    <div className="surface np-review" data-testid="nonprofit-review-detail">
      <dl className="np-facts">
        <dt>Organization</dt><dd>{a.orgName}</dd>
        <dt>Tenant</dt><dd>{a.tenantName} <span className="sa-mono">#{a.tenantId}</span></dd>
        <dt>Country</dt><dd>{a.country}</dd>
        <dt>{a.registrationType === 'ein' ? 'EIN' : 'Registration no.'}</dt>
        <dd>
          {reg ? <span className="sa-mono">{reg.registrationNumber}</span> : <>ending <span className="sa-mono">{a.registrationLast4}</span> <button type="button" className="sa-btn" onClick={reveal}>Show full number</button></>}
        </dd>
        <dt>Validation</dt><dd>{a.validationId ? `${a.validationProvider === 'techsoup' ? 'TechSoup' : 'Percent'}: ${a.validationId}` : '—'}</dd>
        <dt>Website</dt><dd>{a.website ? <a href={a.website} target="_blank" rel="noopener noreferrer nofollow">{a.website}</a> : '—'}</dd>
        <dt>Proof</dt><dd>{a.hasProof ? <a className="sa-btn" href={proofUrl(a.id)} target="_blank" rel="noopener noreferrer">Open {a.proofFileName || 'document'}</a> : 'None uploaded'}</dd>
        <dt>Applicant note</dt><dd>{a.applicantNote || '—'}</dd>
        <dt>Subscription</dt>
        <dd>{d.subscription ? `${d.subscription.planName || 'Plan'} · ${d.subscription.status}${d.subscription.hasStripe ? '' : ' · no Stripe subscription yet'}` : 'None yet'}</dd>
        {a.status === 'approved' && (
          <>
            <dt>Discount</dt>
            <dd>
              {a.discountAppliedOn ? `Applied ${fmt(a.discountAppliedOn)}` : a.discountError ? <span className="sa-count-bad">Not applied: {a.discountError}</span> : 'Waiting for the tenant to subscribe'}
            </dd>
          </>
        )}
      </dl>

      <h4>Decision trail</h4>
      <ol className="np-trail">
        {d.events.map((e) => (
          <li key={e.id}>
            <strong>{ACTION_LABEL[e.action] || e.action}</strong> · {fmt(e.createdOn)}
            {e.actor ? ` · ${e.actor}${e.actorIsPlatform ? ' (On-Screen)' : ''}` : ''}
            {e.note ? <div className="sa-muted">{e.note}</div> : null}
          </li>
        ))}
      </ol>

      {['pending', 'approved'].includes(a.status) && (
        <div className="np-decide">
          <label htmlFor={`np-note-${a.id}`}>Note to the organization {a.status === 'pending' ? '(required to reject)' : '(required to revoke)'}</label>
          <textarea id={`np-note-${a.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} />
          <div className="sa-btn-row">
            {a.status === 'pending' && <button type="button" className="sa-btn primary" disabled={busy} onClick={() => act('approve')}>Approve</button>}
            {a.status === 'pending' && <button type="button" className="sa-btn" disabled={busy || !note.trim()} onClick={() => act('reject')}>Reject</button>}
            {a.status === 'approved' && <button type="button" className="sa-btn" disabled={busy || !note.trim()} onClick={() => act('revoke')}>Revoke discount</button>}
            {a.status === 'approved' && !a.discountAppliedOn && a.discountError && (
              <button type="button" className="sa-btn" disabled={busy} onClick={() => act('retry-discount')}>Retry Stripe coupon</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SuperAdminNonprofitTab() {
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setError('');
    listApplications({ status, q }).then(setData).catch((e) => setError(errorText(e, 'Failed to load applications.')));
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const counts = (data && data.counts) || {};
  const config = data && data.config;

  return (
    <div>
      <div className="sa-section-header">
        <h3>Nonprofit discount</h3>
        <div className="sa-btn-row">
          <RefreshButton onClick={load} />
        </div>
      </div>
      <div className="note">
        {config
          ? `Approving puts the ${config.percentOff}% nonprofit coupon (NONPROFIT_STRIPE_COUPON_ID) on the tenant's Stripe subscription and emails the organization. Reject and revoke need a note, which is emailed to them.`
          : 'Approving puts the nonprofit coupon on the tenant\'s Stripe subscription and emails the organization.'}
      </div>

      <div className="sa-filters np-filters">
        <div className="sa-subtabs" role="tablist" aria-label="Application status">
          {FILTERS.map((f) => (
            <button key={f} type="button" role="tab" aria-selected={status === f} className={`sa-subtab ${status === f ? 'active' : ''}`} onClick={() => { setOpen(null); setStatus(f); }}>
              {f}{f !== 'all' && counts[f] ? ` (${counts[f]})` : ''}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="sa-tenant-select"
          placeholder="Search organization or tenant"
          aria-label="Search organization or tenant"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {msg && <div className="note" role="status"><strong>{msg}</strong></div>}
      {error && <div className="note"><span className="sa-count-bad">{error}</span></div>}
      {!data && !error && <p className="sa-loading">Loading…</p>}
      {data && (
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr><th>Organization</th><th>Tenant</th><th>Registration</th><th>Proof</th><th>Status</th><th>Applied</th><th /></tr>
            </thead>
            <tbody>
              {data.applications.map((a) => (
                <React.Fragment key={a.id}>
                  <tr>
                    <td>{a.orgName}</td>
                    <td>{a.tenantName} <span className="sa-mono">#{a.tenantId}</span></td>
                    <td><span className="sa-mono">{a.country} ••{a.registrationLast4}</span>{a.validationId ? ` · ${a.validationProvider}` : ''}</td>
                    <td>{a.hasProof ? 'Yes' : '—'}</td>
                    <td><span className={`sa-badge ${BADGE[a.status] || ''}`}>{a.status}</span>{a.discountError ? <span className="sa-badge active"> Stripe</span> : null}</td>
                    <td>{fmt(a.createdOn)}</td>
                    <td className="sa-btn-row">
                      <button type="button" className="sa-btn" onClick={() => setOpen(open === a.id ? null : a.id)}>{open === a.id ? 'Hide' : 'Review'}</button>
                    </td>
                  </tr>
                  {open === a.id && (
                    <tr><td colSpan={7}><Detail id={a.id} onChanged={load} notify={setMsg} /></td></tr>
                  )}
                </React.Fragment>
              ))}
              {data.applications.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center' }}>No {status === 'all' ? '' : `${status} `}applications.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
